import _ from 'lodash';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import * as SVG from 'app/shared-components/Icons';
import ListSubheader from '@mui/material/ListSubheader';

import { FormControl, Select, Typography, MenuItem, Checkbox, OutlinedInput, Box } from '@mui/material';

const mapOptions = (options, withCheckbox, value, maxValuesToSelect, onChange) =>
  options.map((option, index) => {
    const checkboxChecked = withCheckbox && value && value.includes(option.value);
    const disable = value?.length >= maxValuesToSelect && !value.includes(option.value);
    return (
      <MenuItem key={index} value={option.value} disabled={disable} onClick={() => onChange?.(option.value)}>
        {withCheckbox && <Checkbox checked={checkboxChecked} />}
        {option?.icon && (
          <Box marginRight="5px">
            <FuseSvgIcon size={16}>{option?.icon}</FuseSvgIcon>
          </Box>
        )}
        <Typography variant="body2">{option.label}</Typography>
      </MenuItem>
    );
  });

const ControlledSelect = ({
  name,
  label,
  control,
  fullWidth = true,
  required,
  multiple,
  placeholder,
  isClearAble,
  options = [],
  withCheckbox,
  iconSize = 16,
  defaultValue,
  onClearClick,
  confirmActions,
  groupedOptions,
  maxValuesToSelect,
  showValuesByNumber,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ control, name });

  const renderOptions = (selected) => {
    if (selected.length === 0) {
      return (
        <Typography variant="body2" color="accent.500">
          {placeholder}
        </Typography>
      );
    }
    if (withCheckbox) {
      if (showValuesByNumber && selected.length > 1) {
        return `${selected.length} items selected`;
      }
      return selected.map((selectedValue) => _.find(options, { value: selectedValue }).label).join(', ');
    }
    return selected;
  };

  const renderValue =
    withCheckbox && multiple
      ? renderOptions
      : placeholder
      ? (selected) => {
          if (!selected || selected.length === 0) {
            return (
              <Typography variant="body2" color="accent.500">
                {placeholder}
              </Typography>
            );
          }
          const found = _.find(options, { value: selected });
          return found ? found.label : selected;
        }
      : undefined;

  return (
    <FormControl fullWidth={fullWidth}>
      <Typography variant="caption" color="accent.500">
        {label}
        {required && '*'}
      </Typography>
      <Select
        open={open}
        displayEmpty
        error={!!error}
        onChange={onChange}
        multiple={multiple}
        defaultValue={defaultValue}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value || defaultValue}
        renderValue={renderValue}
        input={<OutlinedInput placeholder={placeholder} />}
        IconComponent={(props) => (
          <div>
            {isClearAble && value?.length > 0 && (
              <SVG.Feather
                size={iconSize}
                iconId="x"
                sx={{
                  position: 'absolute',
                  right: '30px',
                  cursor: 'pointer',
                  top: 'calc(50% - .5em)',
                  color: 'rgba(0, 0, 0, 0.54)',
                }}
                onClick={() => {
                  onChange('');
                  onClearClick?.();
                }}
              />
            )}
            <SVG.CommonIcons size={iconSize} iconId="cheveron-down" {...props} />
          </div>
        )}
        {...restProps}
      >
        {(groupedOptions &&
          Object.keys(groupedOptions).map((group) => (
            <div key={group}>
              <ListSubheader>{groupedOptions[group].name}</ListSubheader>

              {mapOptions(
                groupedOptions[group].options,
                withCheckbox,
                value,
                maxValuesToSelect,
                (optionValue) => {
                  onChange(optionValue);
                  setOpen(false);
                }
              )}
            </div>
          ))) ||
          mapOptions(options, withCheckbox, value, maxValuesToSelect)}
      </Select>
      {error && (
        <Typography variant="caption" color="error.main">
          {error.message}
        </Typography>
      )}
    </FormControl>
  );
};

export default ControlledSelect;
